import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { verseService } from '../../../services/supabaseService';
import { bibleService } from '../../../services/bibleService';
import { useAuth } from '../../../contexts/AuthContext';
import { checkAndGrantAwards, checkWeeklyVerseStreak } from '../../../services/awardService';
import { useAwardToast } from '../../../contexts/AwardToastContext';

const AddToLibraryModal = ({
  isOpen,
  onClose,
  book,
  chapter,
  version,
  verses,
  selectedVerseNumber = null
}) => {
  const { user } = useAuth();
  const { showAwardToast } = useAwardToast();
  const [startVerse, setStartVerse] = useState(selectedVerseNumber || 1);
  const [endVerse, setEndVerse] = useState(selectedVerseNumber || 1);
  const [phase, setPhase] = useState(1);
  const [selectedVersion, setSelectedVersion] = useState(version || 'KJV');
  const [availableVersions, setAvailableVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const totalVerses = verses?.length || 1;

  // Load available Bible versions
  useEffect(() => {
    const loadVersions = async () => {
      const { data } = await bibleService?.getBibleVersions();
      if (data?.length > 0) {
        setAvailableVersions(data?.filter(v => v?.is_enabled !== false));
      }
    };
    loadVersions();
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const initialVerse = selectedVerseNumber || 1;
      setStartVerse(initialVerse);
      setEndVerse(initialVerse);
      setPhase(1);
      setSelectedVersion(version || 'KJV');
      setError('');
      setSuccess(false);
    }
  }, [isOpen, selectedVerseNumber, version]);

  // Ensure end >= start
  useEffect(() => {
    if (endVerse < startVerse) {
      setEndVerse(startVerse);
    }
  }, [startVerse, endVerse]);

  const getSelectedVerses = () => {
    return verses?.filter(verse =>
      verse?.number >= startVerse && verse?.number <= endVerse
    ) || [];
  };

  const generateReference = () => {
    const bookName = book?.charAt(0)?.toUpperCase() + book?.slice(1);
    if (startVerse === endVerse) {
      return `${bookName} ${chapter}:${startVerse}`;
    }
    return `${bookName} ${chapter}:${startVerse}-${endVerse}`;
  };

  const stripStrongsNumbers = (text) => {
    if (!text) return '';
    return text?.replace(/\{(?:\()?[HG]\d+(?:\))?\}/g, '')?.trim();
  };

  const generateVerseText = () => {
    const selectedVerses = getSelectedVerses();
    if (!selectedVerses || selectedVerses?.length === 0) return '';
    if (selectedVerses?.length === 1) {
      return stripStrongsNumbers(selectedVerses?.[0]?.text || '');
    }
    let combinedText = '';
    selectedVerses?.forEach((verse, index) => {
      const cleanText = stripStrongsNumbers(verse?.text || '');
      if (index === 0) {
        combinedText = cleanText;
      } else {
        combinedText += `  ${verse?.number}  ${cleanText}`;
      }
    });
    return combinedText;
  };

  const getPhaseDescription = (phaseValue) => {
    switch (phaseValue) {
      case 0: return 'Future — not scheduled for review yet';
      case 1: return 'Daily Reviews (every day for 30 days)';
      case 2: return 'Gradual Increase (1–7 day intervals)';
      case 3: return 'Weekly Reviews (every 7 days)';
      case 4: return 'Weekly to Monthly (7–30 day intervals)';
      case 5: return 'Monthly Reviews (every 30 days)';
      case 6: return 'Monthly to Quarterly (30–90 day intervals)';
      case 7: return 'Quarterly Maintenance (every 90 days)';
      default: return 'Unknown Phase';
    }
  };

  const handleAddToLibrary = async () => {
    if (!user) {
      setError('Please log in to add verses to your library.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const reference = generateReference();
      const text = generateVerseText();

      if (!text) {
        setError('No verse text found for the selected range.');
        return;
      }

      const verseCount = endVerse - startVerse + 1;

      const bibleVersionMap = {
        'kjv': 'kjv', 'asv': 'asv', 'niv': 'niv', 'esv': 'esv',
        'nkjv': 'nkjv', 'nasb': 'nasb', 'nlt': 'nlt'
      };

      const today = new Date()?.toISOString()?.split('T')?.[0];
      const versionKey = selectedVersion?.toLowerCase();

      const verseData = {
        user_id: user?.id,
        reference,
        text,
        book,
        chapter,
        verse_start: startVerse,
        verse_end: endVerse,
        verse_count: verseCount,
        bible_version: bibleVersionMap?.[versionKey] || 'niv',
        current_phase: phase,
        current_interval: phase === 0 ? null : 1.0,
        next_review_date: phase === 0 ? null : today
      };

      const { data, error } = await verseService?.createVerse(verseData);

      if (error) {
        if (error?.message?.includes('duplicate')) {
          setError('This verse is already in your library.');
        } else {
          setError(error?.message || 'Failed to add verse to library.');
        }
        return;
      }

      if (user?.id) {
        try {
          const { supabase } = await import('../../../lib/supabase');
          const { count: totalVersesCount } = await supabase?.from('verses')?.select('id', { count: 'exact', head: true })?.eq('user_id', user?.id);
          const { data: allVerses } = await supabase?.from('verses')?.select('book')?.eq('user_id', user?.id);
          const uniqueBooks = new Set((allVerses || []).map(v => v?.book).filter(Boolean))?.size;
          const [addedAwards, milestoneAwards, bookAwards] = await Promise.all([
            checkAndGrantAwards(user?.id, 'VERSE_ADDED', { totalVerses: totalVersesCount || 1 }),
            checkAndGrantAwards(user?.id, 'VERSE_MILESTONE', { totalVerses: totalVersesCount || 1 }),
            checkAndGrantAwards(user?.id, 'BOOK_VARIETY', { uniqueBooks })
          ]);
          [...addedAwards, ...milestoneAwards, ...bookAwards]?.forEach(a => showAwardToast(a));

          // BUG 4: Check WEEKLY_VERSE_STREAK awards — count Phase 1 verses added per week
          // for 10 consecutive weeks
          try {
            const weeklyAwards = await checkWeeklyVerseStreak(user?.id, supabase);
            weeklyAwards?.forEach(a => showAwardToast(a));
          } catch (weeklyErr) {
            console.warn('Weekly verse streak check failed:', weeklyErr);
          }
        } catch (awardErr) {
          console.warn('Award check failed:', awardErr);
        }
      }

      setSuccess(true);
      setTimeout(() => { onClose?.(); }, 1500);

    } catch (err) {
      console.error('Error adding verse to library:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const phasePercent = (phase / 7) * 100;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-background rounded-lg border border-border max-w-sm w-full animate-in fade-in zoom-in-95 duration-200 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
          <h2 className="text-base font-heading font-semibold text-foreground">Add to Library</h2>
          <Button variant="ghost" size="icon" onClick={onClose} disabled={loading} className="h-7 w-7">
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Content */}
        <div className="px-4 py-3 space-y-3">
          {/* Book/Chapter Info */}
          <div className="text-center">
            <h3 className="text-sm font-heading font-medium text-foreground capitalize">
              {book} {chapter}
            </h3>
            <p className="text-xs text-muted-foreground">{totalVerses} verses available</p>
          </div>

          {/* Bible Version Selector */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-1">Bible Version</label>
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e?.target?.value)}
              className="w-full px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              disabled={loading}
            >
              {availableVersions?.length > 0 ? (
                availableVersions?.map(v => (
                  <option key={v?.id} value={v?.short_name}>
                    {v?.short_name} — {v?.version_name}
                  </option>
                ))
              ) : (
                <option value={version}>{version?.toUpperCase()}</option>
              )}
            </select>
          </div>

          {/* Verse Selection */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">Start Verse</label>
              <select
                value={startVerse}
                onChange={(e) => setStartVerse(parseInt(e?.target?.value))}
                className="w-full px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={loading}
              >
                {Array.from({ length: totalVerses }, (_, i) => i + 1)?.map(num => (
                  <option key={num} value={num}>Verse {num}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">End Verse</label>
              <select
                value={endVerse}
                onChange={(e) => setEndVerse(parseInt(e?.target?.value))}
                className="w-full px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={loading}
              >
                {Array.from({ length: totalVerses }, (_, i) => i + 1)
                  ?.filter(num => num >= startVerse)
                  ?.map(num => (
                    <option key={num} value={num}>Verse {num}</option>
                  ))}
              </select>
            </div>
          </div>

          {/* Phase Slider */}
          <div>
            <label className="block text-xs font-medium text-foreground mb-2">
              Memorization Phase: <span className="text-primary font-semibold">{phase}</span>
            </label>
            <div className="relative flex items-center py-1">
              <input
                type="range"
                min="0"
                max="7"
                value={phase}
                onChange={(e) => setPhase(parseInt(e?.target?.value))}
                className="w-full cursor-pointer"
                disabled={loading}
                style={{
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  height: '2px',
                  background: 'var(--border, #e2e8f0)',
                  outline: 'none',
                  borderRadius: '2px',
                }}
              />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span>0 (Future)</span>
              <span>7 (Mastered)</span>
            </div>
            {/* Single phase description */}
            <div className="p-2 bg-muted/30 rounded-md">
              <p className="text-xs text-foreground">
                <span className="font-semibold text-primary">Phase {phase}:</span> {getPhaseDescription(phase)}
              </p>
            </div>
          </div>

          {/* Preview */}
          <div className="border border-border rounded-md p-2.5 bg-muted/20">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-foreground">Preview</span>
              <span className="text-xs text-muted-foreground">{generateReference()}</span>
            </div>
            <div className="max-h-20 overflow-y-auto">
              <p className="text-xs text-foreground leading-relaxed">
                {generateVerseText() || 'No verses selected'}
              </p>
            </div>
          </div>

          {/* Error / Success */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-2">
              <div className="flex items-center space-x-1.5">
                <Icon name="AlertCircle" size={14} className="text-destructive" />
                <p className="text-xs text-destructive">{error}</p>
              </div>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-2">
              <div className="flex items-center space-x-1.5">
                <Icon name="CheckCircle" size={14} className="text-green-600" />
                <p className="text-xs text-green-700">Added to library in Phase {phase}!</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-2 px-4 py-3 border-t border-border">
          <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleAddToLibrary}
            disabled={loading || !user || success}
            className="min-w-[90px]"
          >
            {loading ? (
              <Icon name="Loader2" size={14} className="animate-spin" />
            ) : success ? (
              <Icon name="Check" size={14} />
            ) : (
              `Add — Phase ${phase}`
            )}
          </Button>
        </div>
      </div>
      <style>{`
        input[type='range']::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--primary, #3b82f6);
          border: 2px solid white;
          box-shadow: 0 0 0 1px var(--primary, #3b82f6);
          cursor: pointer;
        }
        input[type='range']::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--primary, #3b82f6);
          border: 2px solid white;
          box-shadow: 0 0 0 1px var(--primary, #3b82f6);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default AddToLibraryModal;